class ProductMultipackWidget {
  constructor(root) {
    this.root = root;
    this.tiles = Array.from(root.querySelectorAll('[data-pmw-tile]'));
    this.container = root.closest('#fast-buy-drawer') || root.closest('.product-information-section') || document;
    this.quantityInput = this.container.querySelector(`[id="${root.dataset.pmwQuantityInput}"]`);
    this.productHandle = root.dataset.pmwProductHandle || '';
    this.priceDisplayMode = root.dataset.pmwPriceDisplayMode || '';
    this.discountSchema = this.parseDiscountSchema(root.dataset.pmwDiscountSchema);

    this.init();
  }

  parseDiscountSchema(rawSchema) {
    if (!rawSchema) return window.ykMultipackDiscountSchema || {};

    try {
      const parsed = JSON.parse(rawSchema);
      return parsed && typeof parsed === 'object' ? parsed : window.ykMultipackDiscountSchema || {};
    } catch (e) {
      return window.ykMultipackDiscountSchema || {};
    }
  }

  init() {
    this.tiles.forEach((tile) => {
      tile.addEventListener('click', () => this.selectQuantity(tile.dataset.pmwQty));

      const bestDealLabel = tile.querySelector('svg + span');
      if (bestDealLabel) bestDealLabel.classList.add('pmw-best-deal-label');
    });

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => this.syncFromQuantityInput());
      this.quantityInput.addEventListener('input', () => this.syncFromQuantityInput());
      this.syncFromQuantityInput();
    }

    if (!this.root.classList.contains('pmw-root--always-visible')) {
      this.watchPurchaseOption();
    }

    this.watchImpression();
    this.watchAddToCart();
    this.watchVariantChange();
  }

  formatMoney(cents) {
    const safeValue = Math.max(0, Number(cents) || 0);
    return (safeValue / 100).toFixed(2).replace('.', ',') + ' zł';
  }

  watchVariantChange() {
    const onVariantChange = (event) => {
      if (event.detail?.container && !event.detail.container.contains(this.root)) return;
      const basePriceCents = Number(event.detail?.currentVariant?.price);
      if (!Number.isFinite(basePriceCents) || basePriceCents <= 0) return;

      this.updateTilePrices(basePriceCents);
    };
    document.addEventListener('onVariantChange', onVariantChange);
    document.addEventListener('quick-buy:variant-change', onVariantChange);
  }

  updateTilePrices(basePriceCents) {
    const discountSchema = this.discountSchema;

    this.tiles.forEach((tile) => {
      const qty = Number(tile.dataset.pmwQty);
      if (!qty) return;

      const discount = Number(discountSchema[String(qty)]) || 0;
      const multiplier = 100 - discount;
      const totalBasePrice = basePriceCents * qty;
      const totalPrice = Math.round((totalBasePrice * multiplier) / 100);
      const unitPrice = Math.round((basePriceCents * multiplier) / 100);

      const totalPriceEl = tile.querySelector('[data-pmw-total-price]');
      const totalBasePriceEl = tile.querySelector('[data-pmw-total-base-price]');
      const savingsEl = tile.querySelector('[data-pmw-savings]');

      if (totalPriceEl) totalPriceEl.textContent = this.formatMoney(totalPrice);
      if (totalBasePriceEl) totalBasePriceEl.textContent = this.formatMoney(totalBasePrice);

      if (savingsEl) {
        if (this.priceDisplayMode === 'total_savings') {
          savingsEl.textContent = `${this.formatMoney(totalBasePrice - totalPrice)} taniej`;
        } else {
          savingsEl.textContent = `${this.formatMoney(unitPrice)} / opak`;
        }
      }
    });
  }

  trackInteraction({ element, elementValue, interactionType }) {
    if (typeof dataLayerPromise !== 'function') return;

    dataLayerPromise({
      event: 'multipack_widget_interaction',
      section: 'product_page',
      element,
      element_value: elementValue,
      interaction_type: interactionType,
    });
  }

  watchImpression() {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          this.trackInteraction({
            element: 'multipack_widget',
            elementValue: this.productHandle,
            interactionType: 'view',
          });
          observerInstance.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(this.root);
  }

  watchAddToCart() {
    document.addEventListener('yk:add_to_cart_custom', (event) => {
      const addedQuantity = Number(event.detail?.product?.quantity);
      const selectedQuantity = Number(this.quantityInput?.value);

      if (!addedQuantity || addedQuantity <= 1 || addedQuantity !== selectedQuantity) return;

      this.trackInteraction({
        element: 'add_to_cart',
        elementValue: `${this.productHandle} - ${addedQuantity}`,
        interactionType: 'add_to_cart',
      });
    });
  }

  selectQuantity(qty) {
    if (!this.quantityInput) return;

    this.root.dataset.pmwUsed = 'true';
    this.quantityInput.value = qty;
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.highlightTile(qty);
    this.trackInteraction({
      element: 'multipack_tile',
      elementValue: `${this.productHandle} - ${qty}`,
      interactionType: 'click',
    });
  }

  syncFromQuantityInput() {
    this.highlightTile(this.quantityInput.value);
  }

  highlightTile(qty) {
    this.tiles.forEach((tile) => {
      const isSelected = tile.dataset.pmwQty === String(qty);
      tile.classList.toggle('border-1', isSelected);
      tile.classList.toggle('border-black', isSelected);
      tile.classList.toggle('border-gray40', !isSelected);
    });
  }

  isOnetimeSelected() {
    const onetimeInput = this.container.querySelector('.rc-radio.onetime-radio input, .rc-radio.rc-option__onetime input');
    if (onetimeInput) return onetimeInput.checked;

    // Products without a subscription option have no purchase-option radios at all.
    return !this.container.querySelector('.subscription-radio');
  }

  updateVisibility() {
    this.root.hidden = !this.isOnetimeSelected();
  }

  watchPurchaseOption() {
    this.updateVisibility();

    document.addEventListener('change', (event) => {
      if (event.target.closest('.rc-radio, .subscription-radio')) {
        this.updateVisibility();
      }
    });

    // ReCharge injects its purchase-option widget asynchronously, so the initial
    // state above can run before it exists. Re-check for a few seconds after load.
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      this.updateVisibility();
      if (attempts >= 20) clearInterval(interval);
    }, 250);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-pmw-root]').forEach((root) => new ProductMultipackWidget(root));
});
